import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import HeaderCard from '../../../components/Header/HeaderCard';
import TableList from '../../../components/Tools/TableList';
import { apiService } from '../../../components/Config/api.service';
import HspForm from './HspForm';
import Drawer from '../../../components/Tools/DrawerCustom';

const Hsp = () => {
    const { t } = useTranslation();
    const QUICK_FILTER_FIELD = 'month';
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        offset: 0,
        limit: 25,
        total_pages: 1,
        total_count: 0
    });
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [filterBy, setFilterBy] = useState({});
    const [overtimeConfigs, setOvertimeConfigs] = useState([]); // NEW: configs dynamiques
    
    const [selectedId, setSelectedId] = useState(null);
    const [drawerKey, setDrawerKey] = useState(0);

    const handleRowClick = (id) => {
        setSelectedId(id);
        setDrawerKey(prev => prev + 1);
        openDrawer();
    };

    const openDrawer = () => {
        setIsDrawerOpen(true);
    };

    const closeDrawer = () => {
        setIsDrawerOpen(false);
        setSelectedId(null);
    };

    // number of decimals for percentage displays
    const DECIMALS = 2;
    const formatNumber = (v) => {
        const n = parseFloat(v);
        if (isNaN(n)) return '0.00';
        return n.toFixed(DECIMALS);
    };

    // Colonnes dynamiques selon les configs + total
    const columns = useMemo(() => {
        const baseCols = [
            { key: 'employee_name', label: t('payslipsList.employee') },
            { key: 'month', label: t('payslipsList.month') },
        ];
        const dynamicCols = (overtimeConfigs || []).map(cfg => ({
            key: `cfg_${cfg.code}`,
            label: cfg.name || cfg.code,
            render: row => {
                const line = (row.overtime_summary || []).find(
                    l => l.config_code === cfg.code || l.config_id === cfg.id
                );
                return formatNumber(line ? line.hours : 0);
            }
        }));
        const totalCol = {
            key: 'overtime_total_hours',
            label: t('hsp.total'),
            render: row => formatNumber(row.overtime_total_hours || 0)
        };
        return [...baseCols, ...dynamicCols, totalCol];
    }, [overtimeConfigs, t]);

    const normalizeFilterBy = (filter = {}) => ({
        ...filter,
        filters: Array.isArray(filter?.filters) ? filter.filters : [],
    });

    const hasActiveFilters = (filter = {}) => normalizeFilterBy(filter).filters.length > 0;

    const toInputMonthValue = (value) => {
        if (!value) return '';
        if (value.includes('/')) {
            const [month, year] = value.split('/');
            if (month && year) return `${year}-${month.padStart(2, '0')}`;
        }
        return value;
    };

    const toApiMonthValue = (value) => {
        if (!value) return '';
        if (value.includes('-')) {
            const [year, month] = value.split('-');
            if (year && month) return `${month}/${year}`;
        }
        return value;
    };

    const getQuickFilterValue = () => {
        const currentFilter = normalizeFilterBy(filterBy).filters.find(item => item.field === QUICK_FILTER_FIELD);
        return currentFilter ? toInputMonthValue(String(currentFilter.value || '')) : '';
    };

    const handleMonthFilterChange = (value) => {
        const normalized = normalizeFilterBy(filterBy);
        const filters = normalized.filters.filter(item => item.field !== QUICK_FILTER_FIELD);

        if (value) {
            filters.push({
                field: QUICK_FILTER_FIELD,
                operator: '=',
                value: toApiMonthValue(value),
            });
        }

        const nextFilter = {
            ...normalized,
            filters,
        };

        setFilterBy(nextFilter);
        handlePageChange(1, searchTerm, nextFilter);
    };

    const monthFilterValue = getQuickFilterValue();
    const monthToolbarFilter = (
        <input
            type="month"
            className="input input-bordered input-sm min-w-[190px] bg-white"
            value={monthFilterValue}
            onChange={(e) => handleMonthFilterChange(e.target.value)}
            aria-label={t('hsp.filterByMonth')}
        />
    );

    const fetchAdditionalHours = (page = 1, search = '', filter = "") => {
        setLoading(true);

        const offset = (page - 1) * pagination.limit;
        const params = new URLSearchParams();
        params.append('offset', offset);
        params.append('limit', pagination.limit);
        if (search) params.append('search', search);
        if (hasActiveFilters(filter)) params.append('filter_by', JSON.stringify(filter));

        apiService.getElement(`/additionalHours?${params.toString()}`)
            .then(response => {
                const responseData = response.data;
                setData(responseData.additionalHours || []);
                setOvertimeConfigs(responseData.additionalHours_config || []); // NEW
                if (responseData.pagination) {
                    const currentPage = Math.floor(responseData.pagination.offset / responseData.pagination.limit) + 1;
                    setPagination({
                        ...responseData.pagination,
                        page: currentPage
                    });
                }
            })
            .catch(error => {
                console.error('There was an error fetching the data!', error);
                setData([]);
                setPagination({
                    offset: 0,
                    limit: 4,
                    total_pages: 1,
                    total_count: 0
                });
            })
            .finally(() => {
                setLoading(false); // moved to finally
            });
    };

    useEffect(() => {
        fetchAdditionalHours(pagination.page, searchTerm, filterBy);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePageChange = (page, search, newFilterBy) => {
        const nextSearch = search !== undefined ? search : searchTerm;
        const nextFilter = newFilterBy !== undefined ? newFilterBy : filterBy;
        setSearchTerm(nextSearch);
        setFilterBy(nextFilter);
        fetchAdditionalHours(page, nextSearch, nextFilter); // fixed
    };

    const refreshHspList = () => fetchAdditionalHours();

    // champs export dynamiques selon les configs
    const exportFields = useMemo(() => {
        const dynamic = (overtimeConfigs || []).map(c => `hours_${c.code}`);
        return ['employee_id', 'month', ...dynamic];
    }, [overtimeConfigs]);

    return (
        <div className="p-6">
            <div className='flex justify-between items-center mb-6'>
                <HeaderCard
                    title={t('leave.overtimeHours')}
                    exportProps={{
                        model_name: "additional_hour",
                        fields: exportFields, // UPDATED
                        filename: "heures_supplementaires_export",
                        importUrl: "/import/additional_hour",
                        onImportSuccess: refreshHspList
                    }}
                />

                <div className="flex justify-between items-center">
                    <button
                        className="btn btn-md bg-[--color-primary] text-white rounded-full mr-2"
                        onClick={() => { 
                            setSelectedId(null); 
                            setDrawerKey(prev => prev + 1);
                            openDrawer(); 
                        }}
                    >
                        {t('hsp.addOvertimeHour')}
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto card bg-white shadow-sm rounded-lg p-2">
                <TableList
                    columns={columns}
                    data={data}
                    onRowClick={row => handleRowClick(row.id)} 
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    searchQuery={searchTerm}
                    setSearchQuery={setSearchTerm}
                    filterBy={filterBy}
                    setFilterBy={setFilterBy}
                    toolbarActions={monthToolbarFilter}
                    showFilter={false}
                    model={"additionalHours"}
                    loading={loading}
                />
            </div>

            <div className={`fixed inset-0 z-50 ${isDrawerOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                <Drawer isOpen={isDrawerOpen} setIsOpen={setIsDrawerOpen} width={600}>
                    <HspForm
                        key={drawerKey}
                        setIsDrawerOpen={closeDrawer}
                        id={selectedId}
                        refreshHspList={refreshHspList}
                    />
                </Drawer>
            </div>
        </div>
    );
};

export default Hsp;
